import React, { useMemo, useState, forwardRef, useRef, useEffect } from 'react';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './DropDown.module.scss';
import AppIcon from '../AppIcon/AppIcon';
import Title from '../TitleStyles/title';
import { icons } from '@/school-management/utils/icons';

export interface Option {
  label: string;
  value: string;
  picture?: keyof typeof icons;

  data?: any;
}

interface DropDownProps {
  options: Option[];
  optionRender?: (option: Option) => any;
  Open?: boolean;
  autoClose?: boolean;
  title?: string;
  placeholder?: string;
  label?: string;
  value?: Option;
  name?: string;
  onChange?: (value: Option) => void;
  onBlur?: () => void;
  inputRef?: React.Ref<any>;
  staticTitle?: string;
  style?: React.CSSProperties;
  isEmptyBorder?: boolean;
  isSeparate?: boolean;
  withHeader?: boolean;
  containerClassName?: string;
  headerClassName?: string;
  listClassName?: string;
  arrowClassName?: string;
  itemClassName?: string;
  multi?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectedOptions?: Option[];
  onChangeMulti?: (values: Option[]) => void;
  autoScrollIntoView?: boolean;
  disabled?: boolean;
}

const DropDown = forwardRef<HTMLDivElement, DropDownProps>(
  (
    {
      options,
      optionRender,
      Open,
      autoClose,
      title,
      placeholder,
      label,
      value,
      name,
      onChange,
      onBlur,
      inputRef,
      staticTitle,
      style,
      isEmptyBorder,
      isSeparate,
      withHeader = false,
      containerClassName,
      headerClassName,
      listClassName,
      arrowClassName,
      itemClassName,
      multi,
      searchable,
      searchPlaceholder,
      selectedOptions = [],
      onChangeMulti,
      autoScrollIntoView,
      disabled,
    },
    ref
  ) => {
    const { t } = useAdminTranslation();
    const [isOpen, setIsOpen] = useState(Open || false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement | null>(null);

    const toggleDropdown = () => setIsOpen(!disabled && !isOpen);
    const hasOptions = Array.isArray(options) && options.length > 0;

    useEffect(() => {
      if (disabled) {
        setIsOpen(false);
      }
    }, [disabled]);

    useEffect(() => {
      if (isOpen && autoClose) {
        const handleOutsideClick = (event: any) => {
          if (containerRef.current && !containerRef.current.contains(event.target)) {
            setIsOpen(false);
          }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
          document.removeEventListener('mousedown', handleOutsideClick);
          document.removeEventListener('touchstart', handleOutsideClick);
        };
      }
    }, [isOpen, autoClose]);

    useEffect(() => {
      if (isOpen && autoScrollIntoView) {
        const timer = setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
          }
        }, 300);

        return () => {
          clearTimeout(timer);
        };
      }
    }, [isOpen, autoScrollIntoView]);

    const filteredOptions = useMemo(() => {
      const base = hasOptions ? options : [];
      if (!searchable || !searchQuery) return base;
      const q = searchQuery.toLowerCase();
      return base.filter(opt => {
        const labelMatch = opt.label?.toLowerCase().includes(q);
        const email = (opt as any)?.data?.email as string | undefined;
        const emailMatch = typeof email === 'string' && email.toLowerCase().includes(q);
        return labelMatch || emailMatch;
      });
    }, [options, hasOptions, searchable, searchQuery]);

    const handleOptionClick = (option: Option) => {
      if (multi) {
        if (!onChangeMulti) return;
        const isSelected = selectedOptions?.some(o => o.value === option.value);
        const next = isSelected
          ? (selectedOptions || []).filter(o => o.value !== option.value)
          : [...(selectedOptions || []), option];
        onChangeMulti(next);
        return;
      }
      if (onChange) onChange(option);
      setIsOpen(false);
    };

    const finalPlaceholder = placeholder || t('dropDown.placeholder');

    const dropdownHeader = (
      <div
        className={classes(styles.dropdownHeader, {
          [styles.open]: isOpen,
          [styles.picture]: options?.[0]?.picture,
          [styles.emptyBorder]: isEmptyBorder,
          [styles.disabled]: disabled,
          [headerClassName || '']: !!headerClassName,
        })}
        onClick={toggleDropdown}
      >
        {options?.[0]?.picture && (
          // {value?.picture != null  && (
          <span>
            <AppIcon icon='portuguese' size={24} />
            {/* <AppIcon icon={icons[value?.picture]} size={24} /> */}
          </span>
        )}
        <span className={styles.label}>
          {staticTitle && <span style={{ color: '#94A3B8', fontWeight: 400 }}>{staticTitle}</span>}
          <span
            style={{
              color: '#08263F',
              opacity: value ? 1 : 0.5,
              fontWeight: 400,
              marginLeft: staticTitle ? 4 : 0,
            }}
          >
            {value ? value.label : finalPlaceholder}
          </span>
        </span>
        <AppIcon
          icon='dropDown'
          className={`${styles.dropdownArrow} ${isOpen ? styles.open : ''} ${arrowClassName || ''}`}
          size={16}
        />
      </div>
    );

    return (
      <div className={styles.inputContainer} style={style} ref={containerRef}>
        {label && <Title subTitle={label} style={{ marginTop: withHeader ? '32px' : '' }} />}
        <div style={{ position: 'relative' }}>
          <div style={{ visibility: 'hidden' }}>{dropdownHeader}</div>
          <div
            className={`${styles.dropdownContainer} ${isOpen ? styles.open : ''} ${containerClassName || ''}`}
            ref={ref}
            onBlur={onBlur}
          >
            {dropdownHeader}
            {isOpen && (
              <ul
                className={`${styles.dropdownList} ${isEmptyBorder ? styles.emptyBorder : ''} ${
                  isSeparate ? styles.separate : ''
                } ${listClassName || ''}`}
              >
                {searchable && (
                  <li
                    className={styles.searchContainer}
                    style={{ cursor: 'default', width: '100%' }}
                  >
                    <div className={styles.inputContainer}>
                      <div className={styles.input}>
                        <input
                          type='search'
                          placeholder={searchPlaceholder || 'Search'}
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                  </li>
                )}

                {(!hasOptions ? [] : filteredOptions).map((option, index) => {
                  const isSelectedSingle = value?.value === option.value;
                  const isSelectedMulti = (selectedOptions || []).some(
                    o => o.value === option.value
                  );
                  const isSelected = multi ? isSelectedMulti : isSelectedSingle;
                  return (
                    <li
                      key={index}
                      className={`${styles.dropdownItem} ${isSelected ? styles.selected : ''} ${itemClassName || ''}`}
                      onClick={() => handleOptionClick(option)}
                    >
                      {multi ? (
                        <>
                          <div className={styles.checkbox}>
                            <input
                              type='checkbox'
                              checked={isSelected}
                              onChange={() => {}}
                              className={styles.checkboxInput}
                            />
                            <span className={styles.checkboxCustom}></span>
                          </div>
                          <span
                            className={styles.optionText}
                            style={{ opacity: isSelected ? 1 : 0.5 }}
                          >
                            {optionRender ? optionRender(option) : option.label}
                          </span>
                        </>
                      ) : (
                        <>
                          {option?.picture != null && (
                            <span>
                              <AppIcon icon='portuguese' size={24} />
                            </span>
                          )}
                          <span className={styles.optionText}>
                            {optionRender ? optionRender(option) : option.label}
                          </span>
                          {isSelected && (
                            <span className={styles.checkmark}>
                              <AppIcon icon='dropDownV' size={24} />
                            </span>
                          )}
                        </>
                      )}
                    </li>
                  );
                })}
                {!hasOptions && (
                  <li className={styles.dropdownItem} style={{ cursor: 'default' }}>
                    <span className={styles.optionText} style={{ opacity: 0.5 }}>
                      No options available
                    </span>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }
);

DropDown.displayName = 'DropDown';

export default DropDown;
